'use client'

import { IconDone } from 'icons/components/IconDone'
import 'icons/utils/constants'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useNotificationsContext } from 'notifications/hooks/useNotificationsContext'
import { useEffect, useMemo, useState } from 'react'
import { NoPurchasesMessage } from 'refunds/components/NoPurchasesMessage/NoPurchasesMessage'
import { SaveErrorModal } from 'refunds/components/SaveErrorModal/SaveErrorModal'
import { SaveRefundDataButton } from 'refunds/components/SaveRefundDataButton/SaveRefundDataButton'
import { SAVE_DATA_STATUS, SNACKBAR_DELAY } from 'refunds/constants/constants'
import { BankContainer } from 'refunds/containers/BankContainer/BankContainer'
import { MoneyCashContainer } from 'refunds/containers/MoneyCashContainer/MoneyCashContainer'
import { RefundSelectorContainer } from 'refunds/containers/RefundSelectorContainer/RefundSelectorContainer'
import { useSaveRefundData } from 'refunds/hooks/useSaveRefundData/useSaveRefundData'
import type {
	BankDataInfo,
	MoneyCashInfo,
	RefundData,
	RefundDataKey,
	VisibilityProps,
} from 'refunds/types/refundData'
import { PageType } from 'types/analytics'

import styles from './RefundsContainer.module.scss'

interface RefundsContainerProps {
	readonly pageType?: PageType
	readonly handleOnSubmit?: (values: BankDataInfo | MoneyCashInfo) => void
	readonly handleOnSelectorChange?: (refundData: RefundData) => void
	readonly refundData: RefundData
	readonly visibilityProps: VisibilityProps
	readonly reloadRefundData: () => void
}

export function RefundsContainer({
	pageType = PageType.Refunds,
	handleOnSubmit,
	handleOnSelectorChange,
	refundData,
	visibilityProps,
	reloadRefundData,
}: RefundsContainerProps) {
	const { t } = useLabels()
	const { addNotification } = useNotificationsContext()

	const {
		country: { countryISO },
	} = useMasterData()

	const { saveRefundData } = useSaveRefundData({ countryISO })

	const [showSaveButton, setShowSaveButton] = useState(true)
	const [saveDataStatus, setSaveDataStatus] = useState<string | null>(null)
	const [refundDataState, setRefundDataState] = useState<RefundData>(refundData)
	const [isLoading, setIsLoading] = useState(false)

	const { bankData, accountBalance, moneyCash } = refundDataState
	const {
		isUniqueMethod,
		isAccountBalanceEnabled,
		showNoPurchaseMessage,
		isSaveButtonEnabled,
	} = visibilityProps

	const {
		isAccountBalanceSelected,
		showBankContainer,
		showMoneyCashContainer,
		hasSelectedOptionPendingData,
	} = useMemo(
		() => ({
			isAccountBalanceSelected: accountBalance?.selected,
			showBankContainer:
				bankData?.available && (bankData?.selected || isUniqueMethod),
			showMoneyCashContainer:
				moneyCash && (moneyCash.selected || isUniqueMethod),
			hasSelectedOptionPendingData:
				(bankData?.selected && !bankData?.bankDataInfo) ||
				(moneyCash?.selected && !moneyCash?.moneyCashInfo),
		}),
		[refundDataState]
	)

	const hasValidRefundOption = Object.values(refundData).some(
		(option) => option && Object.keys(option).length > 0
	)

	useEffect(() => {
		const saveDefaultData = async () => {
			// Save by default the selected accountBalance if it is the only option available
			if (refundDataState) {
				const updatedRefundData = { ...refundDataState }

				;(Object.keys(updatedRefundData) as RefundDataKey[]).forEach((key) => {
					const data = updatedRefundData[key]
					if (data) {
						data.selected = key === 'accountBalance'
					}
				})

				await saveRefundData(updatedRefundData)
			}
		}

		if (
			isUniqueMethod &&
			isAccountBalanceEnabled &&
			!isAccountBalanceSelected
		) {
			saveDefaultData()
		}
	}, [
		isUniqueMethod,
		refundDataState,
		isAccountBalanceEnabled,
		isAccountBalanceSelected,
	])

	useEffect(() => {
		if (refundData) {
			setRefundDataState(refundData)
		}
	}, [refundData])

	if (!refundDataState || !hasValidRefundOption) {
		return null
	}

	const handleOnSaveRequestStatus = (status: string) => {
		setSaveDataStatus(status)
	}

	const resetOnSaveRequestStatus = () => {
		setSaveDataStatus(null)
	}

	const showNotification = () => {
		addNotification({
			id: 'refunds.snackbar.dataSaved',
			dataTestId: 'refunds.snackbar.dataSaved',
			title: t('myRefunds.snackbar.detailsSaved.bodycopy'),
			icon: <IconDone color='content-inverse' />,
			delay: SNACKBAR_DELAY,
			onClose: resetOnSaveRequestStatus,
		})
	}

	const handleSave = async () => {
		if (
			refundDataState &&
			Object.values(refundDataState).some((method) => method?.selected)
		) {
			setIsLoading(true)
			const { isValid } = await saveRefundData(refundDataState)
			setIsLoading(false)
			if (!isValid) {
				return handleOnSaveRequestStatus(SAVE_DATA_STATUS.KO)
			}

			showNotification()
			return handleOnSaveRequestStatus(SAVE_DATA_STATUS.OK)
		}

		return null
	}

	const handleOnChangeFormVisibility = (isFormVisible: boolean) => {
		setShowSaveButton(!isFormVisible && !hasSelectedOptionPendingData)
	}

	const handleOnSelectorChanges = (updatedRefundDataValue: RefundData) => {
		setRefundDataState(updatedRefundDataValue)
		setShowSaveButton(!hasSelectedOptionPendingData)

		if (handleOnSelectorChange) {
			handleOnSelectorChange(updatedRefundDataValue)
		}
	}

	const bankContainerCommonProps = {
		handleOnSubmit,
		showNotification,
		pageType,
		visibilityProps,
		reloadRefundData,
		refundData: refundDataState,
		onChangeFormVisibility: handleOnChangeFormVisibility,
		handleRequestStatus: handleOnSaveRequestStatus,
	}

	return (
		<>
			{showNoPurchaseMessage && <NoPurchasesMessage />}
			{(accountBalance || !isUniqueMethod) && (
				<div
					className={styles.container}
					data-testid='myRefunds.refundOptions.box'
				>
					<RefundSelectorContainer
						refundData={refundDataState}
						onChange={handleOnSelectorChanges}
						isUniqueMethod={!!isUniqueMethod}
					/>
				</div>
			)}

			{showBankContainer && (
				<div
					className={styles.container}
					data-testid='myReturns.request.summary.refundData.bankDetails.box'
				>
					<BankContainer {...bankContainerCommonProps} />
				</div>
			)}

			{showMoneyCashContainer && (
				<div
					className={styles.container}
					data-testid='myReturns.request.summary.refundData.moneyCash.box'
				>
					<MoneyCashContainer {...bankContainerCommonProps} />
				</div>
			)}

			{isSaveButtonEnabled && showSaveButton && (
				<SaveRefundDataButton onSave={handleSave} isLoading={isLoading} />
			)}

			{saveDataStatus === SAVE_DATA_STATUS.KO && (
				<SaveErrorModal
					refundData={refundDataState}
					handleRequestStatus={handleOnSaveRequestStatus}
				/>
			)}
		</>
	)
}
